import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, BookOpen, Users, ShieldCheck, RefreshCw, Ban, Mail } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SiteFooter from '@/components/SiteFooter';
import { AUTHOR, ORG } from '@/config/authorProfile';

const LAST_UPDATED = 'February 2, 2026';

export default function EditorialStandardsPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="container mx-auto px-4 sm:px-6 py-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <AppLogo size={36} />
            <p className="heading text-sm font-semibold tracking-tight text-slate-900">
              How Busy Is My Airport
            </p>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16 max-w-3xl">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-6 pb-8"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-4">
            Editorial standards
          </p>
          <h1 className="heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-4">
            How we cover airports
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
            This is the internal rulebook we hold ourselves to. If we ever break
            one of these rules, we want you to hold us to it — email{' '}
            <a href={`mailto:${AUTHOR.email}`} className="text-blue-600 hover:underline">{AUTHOR.email}</a>{' '}
            with "Correction" in the subject.
          </p>
          <p className="text-xs text-slate-500 mt-4">Last updated: {LAST_UPDATED}</p>
        </motion.section>

        <div className="space-y-5">

          <StandardCard icon={BookOpen} title="1. Sourcing">
            <p>
              Every crowd prediction and airport data point on this site is
              derived from a named primary source, in priority order:
            </p>
            <ul>
              <li><strong>Primary:</strong> FlightAware AeroAPI (real, dated flight schedules and aircraft data)</li>
              <li><strong>Secondary:</strong> FAA Air Traffic Control System Command Center (delay reports)</li>
              <li><strong>Tertiary:</strong> Individual airport-published terminal, TSA wait, and parking data</li>
              <li><strong>Never:</strong> AI-generated statistics, unattributed round numbers, or Wikipedia-scraped filler</li>
            </ul>
            <p>
              If an editorial guide contains an opinion or a personal observation,
              it is labeled as such. Data is data; opinion is opinion.
            </p>
          </StandardCard>

          <StandardCard icon={Users} title="2. Author identity">
            <p>
              Every article carries a real, named byline that links to a{' '}
              <Link to={`/authors/${AUTHOR.slug}`} className="text-blue-600 hover:underline">
                full author page
              </Link>{' '}
              with a photo, credentials, and public contact info. We do not
              publish under pseudonyms and we do not publish AI-generated
              articles without human editorial review and byline responsibility.
            </p>
          </StandardCard>

          <StandardCard icon={ShieldCheck} title="3. Fact-checking">
            <p>
              Before an article is published:
            </p>
            <ul>
              <li>Every airport claim is cross-checked against the airport's own website</li>
              <li>Every numeric statistic is either sourced to a public dataset or generated from our own AeroAPI-backed data</li>
              <li>Every product mention (credit cards, lounges, services) is verified against the official issuer or operator page in the week of publication</li>
              <li>Every embedded link is manually clicked before publish</li>
            </ul>
          </StandardCard>

          <StandardCard icon={RefreshCw} title="4. Updates and freshness">
            <p>
              Airport policies, prices, and TSA rules change. We handle it three ways:
            </p>
            <ul>
              <li><strong>Evergreen guides</strong> (how-to, methodology, FAQs) — reviewed quarterly, timestamped when updated</li>
              <li><strong>Specific-product guides</strong> (credit cards, lounges) — reviewed at least twice a year and re-dated on republish</li>
              <li><strong>Data pages</strong> (live crowd, insights) — regenerated automatically from AeroAPI multiple times per hour</li>
            </ul>
            <p>
              When we make a material update, we add an "Updated:" line to the
              article and log substantive changes in the{' '}
              <Link to="/corrections" className="text-blue-600 hover:underline">
                corrections log
              </Link>.
            </p>
          </StandardCard>

          <StandardCard icon={Ban} title="5. What we don't do">
            <p>
              We do not:
            </p>
            <ul>
              <li>Accept paid placements, sponsored posts, or "review swaps"</li>
              <li>Let affiliate relationships influence which products or airports we cover</li>
              <li>Publish content we have not personally reviewed, whether AI-assisted or not</li>
              <li>Republish or lightly rewrite content from Wikipedia, airline blogs, or competitor sites</li>
              <li>Use scraped or purchased email lists for our newsletter</li>
              <li>Serve interstitial or pop-under ads on any page</li>
            </ul>
          </StandardCard>

          <StandardCard icon={Mail} title="6. Reader input and disputes">
            <p>
              We take reader corrections seriously. If you spot an error:
            </p>
            <ol>
              <li>Email <a href={`mailto:${AUTHOR.email}`} className="text-blue-600 hover:underline">{AUTHOR.email}</a> with "Correction" in the subject, including the article URL and the specific claim</li>
              <li>We reply {AUTHOR.responseTimePromise}</li>
              <li>Confirmed corrections are updated in-line, timestamped, and logged publicly on the{' '}
                <Link to="/corrections" className="text-blue-600 hover:underline">corrections page</Link>
              </li>
              <li>We never quietly delete errors — the log stays public</li>
            </ol>
          </StandardCard>

        </div>

        <div className="mt-10 p-5 bg-slate-100 rounded-lg text-sm text-slate-600 leading-relaxed">
          These standards apply to all content published on {ORG.domain}. They
          are owned and enforced by {AUTHOR.name}, editor. Suggestions to
          strengthen them are welcome at{' '}
          <a href={`mailto:${AUTHOR.email}`} className="text-blue-600 hover:underline">
            {AUTHOR.email}
          </a>.
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function StandardCard({ icon: Icon, title, children }) {
  return (
    <Card className="border-slate-200 bg-white shadow-sm">
      <CardContent className="p-6 sm:p-7">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-3">{title}</h2>
            <div className="prose prose-slate max-w-none text-sm leading-[1.75] text-slate-700 space-y-3
                            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1
                            [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1">
              {children}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
